import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '../AuthContext';
import { Link } from 'react-router-dom';
import { ChevronUp, ChevronDown } from 'lucide-react';

function AssetsPage() {
  const [assets, setAssets] = useState([]);
  const [filteredAssets, setFilteredAssets] = useState([]);
  const emptyProfiles = { deviceType: [], serviceType: [], operatingSystem: [], applicationPlatform: [], application: [] };
  const getEmptyNewAsset = () => ({ host: '', ip: '', network: '', profiles: { ...emptyProfiles } });
  const [newAsset, setNewAsset] = useState(getEmptyNewAsset());
  const [editingAsset, setEditingAsset] = useState(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [dropdownOptions, setDropdownOptions] = useState({});
  const [dropdownSearch, setDropdownSearch] = useState({});
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [ipFilter, setIpFilter] = useState('');
  const [showNotFilled, setShowNotFilled] = useState(true);
  const [networkFilter, setNetworkFilter] = useState('');
  const [networkOptions, setNetworkOptions] = useState([]);
  const dropdownRef = useRef(null);
  const { API_BASE_URL } = useAuth();
  const [questionCounts, setQuestionCounts] = useState({});
  const [sortDirection, setSortDirection] = useState(null);
  const [allProfiles, setAllProfiles] = useState([]);

  const fetchQuestions = () => {
    const token = localStorage.getItem('token');
    fetch(`${API_BASE_URL}questions`, {
      headers: { Authorization: token },
    })
      .then((res) => res.json())
      .then((data) => setQuestions(data))
      .catch((err) => console.error('Error fetching questions:', err));
  };

  const fetchQuestionCounts = () => {
    const token = localStorage.getItem('token');
    fetch(`${API_BASE_URL}questions`, {
      headers: { Authorization: token },
    })
      .then((res) => res.json())
      .then((data) => {
        // Cria um objeto de contagem de perguntas por IP
        const counts = {};
        data.forEach(({ question }) => {
          if (question.id && !question.answer) {
            counts[question.id] = (counts[question.id] || 0) + 1;
          }
        });
        setQuestionCounts(counts);
      })
      .catch((err) => console.error('Error fetching question counts:', err));
  };

  useEffect(() => {
    fetchQuestions();
    fetchAssets();
    fetchDropdownOptions();
    fetchQuestionCounts();

    // Click outside handler to close dropdown
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Filter assets when the IP filter, network filter, or sort option changes
  useEffect(() => {
    filterAssets();
  }, [showNotFilled, ipFilter, networkFilter, assets, sortDirection]);

  const fetchAssets = () => {
    const token = localStorage.getItem('token');
    fetch(API_BASE_URL + 'assets', {
      headers: { Authorization: token },
    })
      .then((res) => res.json())
      .then((data) => {
        setAssets(data);
        setFilteredAssets(data);
        // Extract unique networks
        extractNetworks(data);
      });
  };

  // Function to extract network options from assets
  const extractNetworks = (assetsList) => {
    const networks = new Set();

    assetsList.forEach(asset => {
      if (asset.network && asset.network.trim()) {
        networks.add(asset.network.trim());
      }
    });

    setNetworkOptions(Array.from(networks).sort());
  };

  const fetchDropdownOptions = () => {
    const token = localStorage.getItem('token');
    fetch(API_BASE_URL + 'profiles', {
      headers: { Authorization: token },
    })
      .then((res) => res.json())
      .then((profiles) => {
        // Group profiles by mapped layer name for compatibility with asset dropdowns
        const layerMapping = {
          "device_type": "deviceType",
          "service": "serviceType",
          "os": "operatingSystem",
          "app_platform": "applicationPlatform",
          "application": "application"
        };
        
        const grouped = {
          deviceType: [],
          serviceType: [],
          operatingSystem: [],
          applicationPlatform: [],
          application: []
        };

        if (Array.isArray(profiles)) {
          profiles.forEach(p => {
            const clientField = layerMapping[p.layer];
            if (clientField && grouped[clientField]) {
              grouped[clientField].push(p.name);
            }
          });
          
          // Sort each list for consistency
          Object.keys(grouped).forEach(key => grouped[key].sort());
        }

        setAllProfiles(profiles);
        setDropdownOptions(grouped);

        // Initialize search state for each dropdown
        const searchState = {};
        Object.keys(grouped).forEach(key => {
          searchState[key] = '';
        });
        setDropdownSearch(searchState);
      })
      .catch(error => console.error('Error fetching dropdown options:', error));
  };

  // Filter assets by IP/Hostname and network, and sort if enabled
  const filterAssets = () => {
    let filtered = [...assets];

    // Filter by IP or Hostname if provided
    if (ipFilter.trim()) {
      const term = ipFilter.trim();
      // Build an IP prefix regex (e.g. "192.168" → /^192\.168/)
      const ipParts = term.split('.');
      let ipPattern = '';
      for (let i = 0; i < ipParts.length; i++) {
        if (ipParts[i] === '') break;
        ipPattern += (i > 0 ? '\\.' : '') + ipParts[i];
      }
      const ipRegex = ipPattern ? new RegExp('^' + ipPattern) : null;
      const hostTerm = term.toLowerCase();
      filtered = filtered.filter(asset =>
        (ipRegex && ipRegex.test(asset.ip)) ||
        (asset.host && asset.host.toLowerCase().includes(hostTerm))
      );
    }

    // Filter by network if provided
    if (networkFilter.trim()) {
      filtered = filtered.filter(asset =>
        asset.network && asset.network.toLowerCase() === networkFilter.toLowerCase()
      );
    }

    if (showNotFilled) {
      filtered = filtered.filter(asset =>
        !asset.profiles || asset.profiles == {} || Object.keys(asset.profiles).length === 0 || (
          Array.isArray(asset.profiles.deviceType) && asset.profiles.deviceType.length === 0 &&
          Array.isArray(asset.profiles.serviceType) && asset.profiles.serviceType.length === 0 &&
          Array.isArray(asset.profiles.operatingSystem) && asset.profiles.operatingSystem.length === 0
        )
      );
    }

    // Sort alphabetically by hostname if enabled
    if (sortDirection == 'asc') {
      filtered.sort((a, b) => a.host.localeCompare(b.host));
    }
    if (sortDirection == 'desc') {
      filtered.sort((a, b) => b.host.localeCompare(a.host));
    }

    setFilteredAssets(filtered);
  };

  const toggleSorting = () => {
    if (sortDirection === null) {
      setSortDirection('asc');
    } else if (sortDirection === 'asc') {
      setSortDirection('desc');
    } else {
      setSortDirection(null);
    }
  };

  const handleNetworkChange = (value) => {
    setNetworkFilter(value);
  };

  // Modified to handle arrays for multi-select
  const handleProfileChange = (asset, setAsset, index, field, value, isMulti = true) => {
    const updatedProfiles = { ...asset.profiles };

    if (isMulti) {
      // For multi-select, toggle the value in the array
      if (!Array.isArray(updatedProfiles[field])) {
        // Convert to array if it's not already (for backwards compatibility)
        updatedProfiles[field] = updatedProfiles[field] ? [updatedProfiles[field]] : [];
      }

      if (updatedProfiles[field].includes(value)) {
        // Remove value if already selected
        updatedProfiles[field] = updatedProfiles[field].filter(item => item !== value);
      } else {
        // Add value if not already selected
        updatedProfiles[field] = [...updatedProfiles[field], value];
      }
    } else {
      // For single select (not used in this implementation, but kept for flexibility)
      updatedProfiles[field] = value;
    }

    setAsset({ ...asset, profiles: updatedProfiles });
  };

  const handleSaveAsset = () => {
    const token = localStorage.getItem('token');
    fetch(API_BASE_URL + 'assets', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: token },
      body: JSON.stringify(newAsset),
    })  
      .then(() => {
        fetchAssets();
        setNewAsset({ host: '', ip: '', network: '', profiles: { ...emptyProfiles } });
        setAddModalOpen(false); // Close the modal
      });
  };

  const handleDeleteAsset = (asset) => {
    if (!window.confirm(`You are going to delete asset "${asset.host}"?`)) {
      return;
    }
    const token = localStorage.getItem('token');
    fetch(API_BASE_URL + `assets/${asset.id}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json', Authorization: token },
      body: JSON.stringify(editingAsset),
    })
      .then(() => {
        fetchAssets();
      });
  };

  const handleEditAsset = (asset) => {
    // Ensure all profile fields are arrays for multi-select
    const profiles = { ...asset.profiles };
    const fields = ['deviceType', 'serviceType', 'operatingSystem', 'applicationPlatform', 'application'];

    fields.forEach(field => {
      if (!Array.isArray(profiles[field])) {
        profiles[field] = profiles[field] ? [profiles[field]] : [];
      }
    });

    // Make sure network is included, even if it's empty, and initialise clientNotes
    const assetWithNetwork = {
      ...asset,
      network: asset.network || '',
      clientNotes: asset.clientNotes || '',
      profiles
    };

    setEditingAsset(assetWithNetwork);
  };

  const handleSaveEditedAsset = () => {
    const token = localStorage.getItem('token');
    fetch(API_BASE_URL + `assets/${editingAsset.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: token },
      body: JSON.stringify(editingAsset),
    })
      .then(() => {
        fetchAssets();
        setEditingAsset(null); // Close the modal
      });
  };

  const handleSearchChange = (field, value) => {
    setDropdownSearch({
      ...dropdownSearch,
      [field]: value
    });
  };

  const toggleDropdown = (dropdownId) => {
    if (activeDropdown === dropdownId) {
      setActiveDropdown(null);
    } else {
      setActiveDropdown(dropdownId);
      // Clear search for the activated dropdown
      setDropdownSearch({
        ...dropdownSearch,
        [dropdownId.split('-')[0]]: ''
      });
    }
  };

  // Filter options based on search
  const getFilteredOptions = (field) => {
    const searchTerm = dropdownSearch[field] || '';
    const options = dropdownOptions[field] || [];

    if (!searchTerm) return options;

    return options.filter(option =>
      option.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  // Get display value for selected options
  const getDisplayValue = (asset, field) => {
    const values = asset.profiles[field];

    if (!values || (Array.isArray(values) && values.length === 0)) {
      return 'Select options';
    }

    if (Array.isArray(values)) {
      return values.join(', ');
    }

    return values;
  };

  // Check if a value is selected
  const isValueSelected = (asset, field, value) => {
    const values = asset.profiles[field];
    if (!values) return false;

    if (Array.isArray(values)) {
      return values.includes(value);
    }

    return values === value;
  };

  // Clear all selections for a field
  const clearSelections = (asset, setAsset, field) => {
    const updatedProfiles = { ...asset.profiles };
    updatedProfiles[field] = [];
    setAsset({ ...asset, profiles: updatedProfiles });
  };

  // Clear filters
  const clearFilters = () => {
    setIpFilter('');
    setNetworkFilter('');
  };

  // Dropdown component for both modals
  const MultiSelectDropdown = ({
    asset,
    setAsset,
    index,
    field,
    label,
    modalType // 'new' or 'edit'
  }) => {
    const dropdownId = `${field}-${modalType}-${index}`;
    const isActive = activeDropdown === dropdownId;

    return (
      <div className="dropdown" ref={isActive ? dropdownRef : null}>
        <label>{label}:</label>
        <div className="position-relative">
          <button
            type="button"
            className="form-select text-start d-flex justify-content-between align-items-center"
            onClick={() => toggleDropdown(dropdownId)}
          >
            <span className="text-truncate">
              {getDisplayValue(asset, field)}
            </span>
          </button>

          {isActive && (
            <div className="dropdown-menu d-block w-100 position-absolute" style={{ maxWidth: '100%' }}>
              <div className="px-2 py-1">
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Search..."
                  value={dropdownSearch[field] || ''}
                  onChange={(e) => handleSearchChange(field, e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  autoFocus
                />
              </div>
              <div className="d-flex justify-content-between align-items-center px-2 py-1">
                <small className="text-muted">
                  {Array.isArray(asset.profiles[field]) ? asset.profiles[field].length : 0} selected
                </small>
                <button
                  type="button"
                  className="btn btn-sm btn-link py-0"
                  onClick={() => clearSelections(asset, setAsset, field)}
                >
                  Clear all
                </button>
              </div>
              <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                {getFilteredOptions(field).map((option, idx) => (
                  <div
                    key={idx}
                    className={`dropdown-item d-flex align-items-center ${isValueSelected(asset, field, option) ? 'active' : ''}`}
                    onClick={() => handleProfileChange(asset, setAsset, index, field, option)}
                  >
                    <div className="form-check mb-0">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={isValueSelected(asset, field, option)}
                        onChange={(e) => { e.stopPropagation(); handleProfileChange(asset, setAsset, index, field, option); }}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <label className="form-check-label">{option}</label>
                    </div>
                  </div>
                ))}
                {getFilteredOptions(field).length === 0 && (
                  <div className="dropdown-item text-muted">No results found</div>
                )}
              </div>
              <div className="border-top mt-2 pt-2 px-2 d-flex justify-content-end">
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  onClick={() => setActiveDropdown(null)}
                >
                  Done
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderProfileBadges = (profileNames) => {
    if (!profileNames) return "-";
    const names = Array.isArray(profileNames) ? profileNames : [profileNames];
    if (names.length === 0) return "-";

    return (
      <div className="d-flex flex-wrap gap-1">
        {names.map((name, idx) => {
          const profile = allProfiles.find(p => p.name === name);
          const isNew = profile ? profile.is_new : false;
          const description = profile ? profile.description : "No description available";

          return (
            <span
              key={idx}
              className="badge"
              style={{
                backgroundColor: isNew ? '#007bff' : '#e3f2fd',
                color: isNew ? 'white' : '#0056b3',
                cursor: 'help',
                fontSize: '0.75rem',
                border: isNew ? 'none' : '1px solid #007bff33'
              }}
              title={description}
            >
              {name}
            </span>
          );
        })}
      </div>
    );
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Assets</h2>
        <div className="input-group" style={{ maxWidth: '300px' }}>
          <span className="input-group-text">Filter</span>
          <input
            type="text"
            className="form-control"
            placeholder="Filter by IP or Hostname"
            value={ipFilter}
            onChange={(e) => setIpFilter(e.target.value)}
          />
        </div>
      </div>

      <div className="d-flex justify-content-between align-items-center mb-3">
        <button
          className="btn btn-primary"
          onClick={() => { setNewAsset(getEmptyNewAsset()); setAddModalOpen(true); }}
        >
          Add Asset
        </button>

        <button
          onClick={() => setShowNotFilled((prev) => !prev)}
          style={{
            backgroundColor: showNotFilled ? '#ff4d4d' : '#4CAF50',
            color: 'white',
            padding: '8px 12px',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
          }}
        >
          {showNotFilled ? 'Show All' : 'Show empty only'}
        </button>

        <div className="d-flex align-items-center">
          <div className="input-group me-2" style={{ maxWidth: '250px' }}>
            <span className="input-group-text">Network</span>
            <select
              className="form-select"
              value={networkFilter}
              onChange={(e) => handleNetworkChange(e.target.value)}
            >
              <option value="">All Networks</option>
              {networkOptions.map((network, index) => (
                <option key={index} value={network}>
                  {network}
                </option>
              ))}
            </select>
          </div>

          {(ipFilter || networkFilter) && (
            <button
              className="btn btn-outline-secondary"
              type="button"
              onClick={clearFilters}
            >
              Clear Filters
            </button>
          )}
        </div>
      </div>

      <table className="table">
        <thead>
          <tr>
            <th
              onClick={toggleSorting}
            >
              <div className="flex items-center space-x-2">
                <span>Hostname</span>
                {sortDirection === 'asc' && <ChevronUp style={{ float: 'right' }} className="w-4 h-4 text-blue-500" />}
                {sortDirection === 'desc' && <ChevronDown style={{ float: 'right' }} className="w-4 h-4 text-blue-500" />}
                {sortDirection === null && <span style={{ float: 'right' }} className="w-4 h-4 opacity-30">⋮</span>}
              </div>
            </th>
            <th>Network</th>
            <th>Device Type</th>
            <th>OS</th>
            <th>Service</th>
            <th>App. Platform</th>
            <th>Application</th>
            <th>Questions</th>
            <th>Links</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredAssets.map((asset, index) => (
            <tr key={index}>
              <td>
                <span
                  style={{ paddingRight: 10, fontWeight: 'bold' }}>
                  {asset.host}
                </span>
                ({asset.ip})
              </td>
              <td>{asset.network || "-"}</td>
              <td>{renderProfileBadges(asset.profiles.deviceType)}</td>
              <td>{renderProfileBadges(asset.profiles.operatingSystem)}</td>
              <td>{renderProfileBadges(asset.profiles.serviceType)}</td>
              <td>{renderProfileBadges(asset.profiles.applicationPlatform)}</td>
              <td>{renderProfileBadges(asset.profiles.application)}</td>
              <td>
                {questionCounts[asset.host] && (
                  <Link
                    to={`/questions?id=${asset.host}`}
                    style={{ borderRadius: 5, padding: 5, backgroundColor: 'red', color: 'white', fontWeight: 'bold' }}
                  >
                    {questionCounts[asset.host]}
                  </Link>
                ) || ''}
              </td>
              <td>
                <Link
                  to={`/logs?id=${asset.id}`}
                  style={{ borderRadius: 5, padding: 5, marginRight: 5, color: 'blue', fontWeight: 'bold' }}
                >
                  Logs
                </Link>
                <Link
                  to={`/alerts?ip=${asset.ip}`}
                  style={{ borderRadius: 5, padding: 5, marginRight: 5, color: 'red', fontWeight: 'bold' }}
                >
                  Alerts
                </Link>
              </td>
              <td>

                <button
                  className="btn btn-warning"
                  onClick={() => handleEditAsset(asset)}
                >
                  Edit
                </button>
                <button
                  className="btn btn-delete"
                  style={{ backgroundColor: '#950606', color: 'white', margin: 5 }}
                  onClick={() => handleDeleteAsset(asset)}
                >
                  Delete
                </button>
              </td>
            </tr>
          ))}
          {filteredAssets.length === 0 && (
            <tr>
              <td colSpan="10" className="text-center py-5">
                {(ipFilter || networkFilter) ?
                  <div>
                    <p>No assets found matching the current filters</p>
                    <button className="btn btn-sm btn-outline-secondary" onClick={clearFilters}>
                      Clear filters
                    </button>
                  </div>
                  :
                  <p>No assets found. Click "Add Asset" to create one.</p>
                }
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {/* Add Asset Modal */}
      {
        addModalOpen && (
          <div
            className="modal show"
            style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}
          >
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Add Asset</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => { setAddModalOpen(false); setNewAsset(getEmptyNewAsset()); }}
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label>Hostname:</label>
                      <input
                        className="form-control"
                        value={newAsset.host}
                        onChange={(e) => setNewAsset({ ...newAsset, host: e.target.value })}
                      />
                    </div>
                    <div className="col-md-6">
                      <label>IP:</label>
                      <input
                        className="form-control"
                        value={newAsset.ip}
                        onChange={(e) => setNewAsset({ ...newAsset, ip: e.target.value })}
                      />
                    </div>
                    <div className="col-md-6">
                      <label>Network:</label>
                      <input
                        className="form-control"
                        value={newAsset.network}
                        onChange={(e) => setNewAsset({ ...newAsset, network: e.target.value })}
                        placeholder="e.g. DMZ, Internal, Production"
                      />
                    </div>
                  </div>
                  <p>Profiles:</p>
                  <div className="border p-2 mb-2">
                    <div className="row mb-2">
                      <div className="col">
                        <MultiSelectDropdown
                          asset={newAsset}
                          setAsset={setNewAsset}
                          index={0}
                          field="deviceType"
                          label="Device Type"
                          modalType="new"
                        />
                      </div>
                      <div className="col">
                        <MultiSelectDropdown
                          asset={newAsset}
                          setAsset={setNewAsset}
                          index={1}
                          field="serviceType"
                          label="Service Type"
                          modalType="new"
                        />
                      </div>
                    </div>
                    <div className="row mb-2">
                      <div className="col">
                        <MultiSelectDropdown
                          asset={newAsset}
                          setAsset={setNewAsset}
                          index={2}
                          field="operatingSystem"
                          label="Operating System"
                          modalType="new"
                        />
                      </div>
                      <div className="col">
                        <MultiSelectDropdown
                          asset={newAsset}
                          setAsset={setNewAsset}
                          index={3}
                          field="applicationPlatform"
                          label="Application Platform"
                          modalType="new"
                        />
                      </div>
                      <div className="col">
                        <MultiSelectDropdown
                          asset={newAsset}
                          setAsset={setNewAsset}
                          index={4}
                          field="application"
                          label="Application"
                          modalType="new"
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-primary" onClick={handleSaveAsset}>
                    Save Asset
                  </button>
                  <button
                    className="btn btn-secondary"
                    onClick={() => { setAddModalOpen(false); setNewAsset(getEmptyNewAsset()); }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      }

      {/* Edit Asset Modal */}
      {
        editingAsset && (
          <div
            className="modal show"
            style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}
          >
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Edit Asset</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setEditingAsset(null)}
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label>Hostname:</label>
                      <input
                        className="form-control"
                        value={editingAsset.host}
                        onChange={(e) =>
                          setEditingAsset({ ...editingAsset, host: e.target.value })
                        }
                      />
                    </div>
                    <div className="col-md-6">
                      <label>Network:</label>
                      <input
                        className="form-control"
                        value={editingAsset.network}
                        onChange={(e) =>
                          setEditingAsset({ ...editingAsset, network: e.target.value })
                        }
                        placeholder="e.g. DMZ, Internal, Production"
                      />
                    </div>
                  </div>
                  <p>Profiles:</p>
                  {
                    <div className="border p-2 mb-2">
                      <div className="row mb-2">
                        <div className="col">
                          <MultiSelectDropdown
                            asset={editingAsset}
                            setAsset={setEditingAsset}
                            index={0}
                            field="deviceType"
                            label="Device Type"
                            modalType="edit"
                          />
                        </div>
                        <div className="col">
                          <MultiSelectDropdown
                            asset={editingAsset}
                            setAsset={setEditingAsset}
                            index={0}
                            field="serviceType"
                            label="Service Type"
                            modalType="edit"
                          />
                        </div>
                      </div>
                      <div className="row mb-2">
                        <div className="col">
                          <MultiSelectDropdown
                            asset={editingAsset}
                            setAsset={setEditingAsset}
                            index={0}
                            field="operatingSystem"
                            label="Operating System"
                            modalType="edit"
                          />
                        </div>
                        <div className="col">
                          <MultiSelectDropdown
                            asset={editingAsset}
                            setAsset={setEditingAsset}
                            index={0}
                            field="applicationPlatform"
                            label="Application Platform"
                            modalType="edit"
                          />
                        </div>
                        <div className="col">
                          <MultiSelectDropdown
                            asset={editingAsset}
                            setAsset={setEditingAsset}
                            index={0}
                            field="application"
                            label="Application"
                            modalType="edit"
                          />
                        </div>
                      </div>
                    </div>
                  }
                  <div className="mb-3 mt-3">
                    <label htmlFor="edit-client-notes">Notes:</label>
                    <textarea
                      id="edit-client-notes"
                      className="form-control"
                      rows={4}
                      placeholder="Add any notes about this asset..."
                      value={editingAsset.clientNotes || ''}
                      onChange={(e) =>
                        setEditingAsset({ ...editingAsset, clientNotes: e.target.value })
                      }
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-primary" onClick={handleSaveEditedAsset}>
                    Save Changes
                  </button>
                  <button
                    className="btn btn-secondary"
                    onClick={() => setEditingAsset(null)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      }
    </div>
  );
}

export default AssetsPage;
